import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { feature } from 'topojson-client';
import { Grid, IconButton } from '@mui/material';
import Tooltip from '../tooltip';
import MunicipioModal from '../municipioModal';
import municipiosData from '../municipios.json';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PainelCrack from './painelCrack';
import { useDevice } from 'components/DeviceContext.js';
import { getColorByResponse } from 'services/colorService'

const MapaCrack = () => {
    const svgRef = useRef();
    const zoomRef = useRef(null);
    const { isMobile } = useDevice();
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const [tooltipContent, setTooltipContent] = useState('');
    const [selectedUF, setSelectedUF] = useState(null);
    const [selectedMunicipio, setSelectedMunicipio] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [locationIconCoords, setLocationIconCoords] = useState(null);
    const [currentColor, setCurrentColor] = useState(null);
    const [legendData, setLegendData] = useState([]);
    const [transform, setTransform] = useState(d3.zoomIdentity);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/mapa-crack`);
                const data = await response.json();
                setLegendData(data);

                const geojsonData = feature(municipiosData, municipiosData.objects.municipios);
                const filteredGeojsonData = selectedUF ? geojsonData.features.filter(feature => feature.properties.uf === selectedUF) : geojsonData.features;

                const width = svgRef.current.clientWidth;
                const height = svgRef.current.clientHeight;

                const projection = d3.geoMercator().fitSize([width, height], geojsonData);
                const path = d3.geoPath().projection(projection);

                const svg = d3.select(svgRef.current)
                    .attr('viewBox', `0 0 ${width} ${height}`)
                    .attr('preserveAspectRatio', 'xMidYMid meet');

                svg.selectAll('*').remove();

                svg.selectAll('path')
                    .data(filteredGeojsonData)
                    .enter()
                    .append('g')
                    .append('path')
                    .attr('d', path)
                    .style('fill', d => {
                        const ibge = Number(d.properties.ibge);
                        const municipio = data.find(item => item.ibge === ibge);
                        const resposta = municipio ? municipio.resposta : null;
												const color = resposta ? getColorByResponse(resposta) : 'var(--sem-cor)'
                        return color;
                    })
                    .style('stroke', '#fff')
                    .style('stroke-width', '0.2px')
                    .on('mouseover', (event, d) => {
                        const [x, y] = d3.pointer(event);
                        setTooltipPos({ x, y });
                        setTooltipContent(d.properties.nome);
                        const ibge = Number(d.properties.ibge);
                        const municipio = data.find(item => item.ibge === ibge);
                        const resposta = municipio ? municipio.resposta : null;
												const color = resposta ? getColorByResponse(resposta) : 'steelblue'
                        setCurrentColor(color);
                    })
                    .on('mouseout', () => {
                        setTooltipContent('');
                    })
                    .on('click', (event, d) => {
                        const { nome, ibge } = d.properties;
                        setModalData({ nome, ibge });
                        setIsModalOpen(true);

                        const [x, y] = path.centroid(d);
                        setLocationIconCoords({ x, y });
                    });

                if (selectedUF) {
                    const ufData = geojsonData.features.filter(feature => feature.properties.uf === selectedUF);
                    const bounds = path.bounds({ type: 'FeatureCollection', features: ufData });
                    const dx = bounds[1][0] - bounds[0][0];
                    const dy = bounds[1][1] - bounds[0][1];
                    const x = (bounds[0][0] + bounds[1][0]) / 2;
                    const y = (bounds[0][1] + bounds[1][1]) / 2;
                    const scale = Math.max(1, Math.min(8, 0.9 / Math.max(dx / width, dy / height)));
                    const translate = [width / 2 - scale * x, height / 2 - scale * y];

                    svg.transition()
                        .duration(750)
                        .call(
                            zoomRef.current.transform,
                            d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)
                        );
                }

                const zoom = d3.zoom()
                    .scaleExtent([1, 8])
                    .on('zoom', (event) => {
                        svg.selectAll('path').attr('transform', event.transform);
                        setTransform(event.transform);
                    });

                svg.call(zoom);
                zoomRef.current = zoom;

            } catch (error) {
                console.error('Erro ao carregar os dados:', error);
            }
        };

        fetchData();
    }, [selectedUF, selectedMunicipio]);

    useEffect(() => {
        if (selectedMunicipio) {
            const geojsonData = feature(municipiosData, municipiosData.objects.municipios);
            const municipio = geojsonData.features.find(feature => feature.properties.nome === selectedMunicipio);
            if (municipio) {
                const path = d3.geoPath().projection(d3.geoMercator().fitSize([svgRef.current.clientWidth, svgRef.current.clientHeight], geojsonData));
                const [x, y] = path.centroid(municipio);
                setLocationIconCoords({ x, y });

                const { nome, ibge } = municipio.properties;
                setModalData({ nome, ibge });
                setIsModalOpen(true);
            }
        }
    }, [selectedMunicipio]);

    const closeModal = () => {
        setIsModalOpen(false);
        setModalData(null);
    };

    const handleFilterChange = (selectedUF) => {
        setSelectedUF(selectedUF);
        setSelectedMunicipio(null);
    };

    const handleMunicipioSelect = (selectedMunicipio) => {
        setSelectedMunicipio(selectedMunicipio);
    };

    const handleZoomIn = () => {
        const svg = d3.select(svgRef.current);
        svg.transition().call(zoomRef.current.scaleBy, 1.3);
    };

    const handleZoomOut = () => {
        const svg = d3.select(svgRef.current);
        svg.transition().call(zoomRef.current.scaleBy, 1 / 1.3);
    };

    return (
        <Grid container style={{ display: 'flex', justifyContent: 'center', backgroundColor: 'var(--azul)' }}>
            <Grid item lg={12} style={{ display: 'flex', justifyContent: 'center', paddingBottom: '0.6em', backgroundColor: 'rgba(83, 108, 149, 0.5)', borderRadius: '0 0 50px 50px', marginBottom: '1em', flexWrap: 'wrap' }}>
                <Grid item lg={2}>
                    <p>MAPA DO CRACK</p>
                </Grid>
                <Grid item lg={6}>
                    <p style={{ fontSize: '14px', textAlign: 'center' }}>O crack está cada vez mais presente nas pequenas cidades e zonas rurais. Veja um retrato de como a droga está afetando os Municípios brasileiros e descubra as principais regiões atingidas.</p>
                </Grid>
            </Grid>
            <Grid item xs={12} md={2} style={{ marginTop: '16px', width: 'auto' }}>
                <PainelCrack
                    municipios={municipiosData.objects.municipios.geometries}
                    onChange={handleFilterChange}
                    onSelectMunicipio={handleMunicipioSelect}
                    legendData={legendData}
                    selectedUF={selectedUF}
                    currentColor={currentColor}
                />
            </Grid>
            <Grid item xs={12} md={1} lg={5} style={{ position: 'relative', minHeight: '62vh', justifyContent: 'center', paddingBottom: isMobile ? '0' : '2em' }}>
                <svg ref={svgRef} style={{ width: '100%', height: '100%' }}></svg>
                <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', flexDirection: 'column' }}>
                    <IconButton onClick={handleZoomIn}>
                        <AddIcon />
                    </IconButton>
                    <IconButton onClick={handleZoomOut}>
                        <RemoveIcon />
                    </IconButton>
                </div>
                {locationIconCoords && (
                    <div
                        style={{
                            position: 'absolute',
                            left: transform.applyX(locationIconCoords.x) - 7,
                            top: transform.applyY(locationIconCoords.y) - 7,
                            transformOrigin: 'center'
                        }}
                    >
                        <LocationOnIcon style={{ fontSize: '20px', color: 'red' }} />
                    </div>
                )}
                {tooltipContent && <Tooltip x={tooltipPos.x} y={tooltipPos.y} content={tooltipContent} />}
                {isModalOpen && modalData && <MunicipioModal municipioData={modalData} onClose={closeModal} />}
            </Grid>
        </Grid>
    );
};

export default MapaCrack;
